import {remCalc} from '../../_utils'

// Group-link overrides shared by the templates whose group links are left-aligned on md+
const leftAlignedGroupText = {
    titleContainer: {
        textAlign: {md: 'left'},
    },
    groupLinkButton: {
        textAlign: {md: 'left'},
        justifyContent: {md: 'flex-start'},
        whiteSpace: {md: 'normal'},
    },
}

const fullWidthGroupLists = {
    groupListContainer: {
        width: {md: '100%'},
        alignItems: {md: 'flex-start'},
    },
    groupLinkContainer: {
        width: {md: '100%'},
        alignItems: {md: 'flex-start'},
    },
}

export default {
    baseStyle: {
        // The whole template scrolls when its content (e.g. many category groups) overflows.
        // The 4px padding/negative margin leaves room for link focus rings that overflow would clip.
        templateContainer: {
            width: `calc(100% + ${remCalc(8)})`,
            minHeight: `calc(${remCalc(600)} + ${remCalc(8)})`,
            maxHeight: `calc(${remCalc(600)} + ${remCalc(8)})`,
            padding: remCalc(4),
            margin: remCalc(-4),
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            sx: {
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {display: 'none'},
            },
        },
        layout: {
            flexDirection: 'row',
            gap: remCalc(20),
            width: '100%',
            height: '100%',
        },
        gridInnerContainer: {
            flexDir: 'column',
            flex: '1 2 0',
            minW: '0',
            minHeight: '100%',
            gap: 'small.base',
        },
        groupOuterContainer: {
            maxWidth: remCalc(347),
            minHeight: remCalc(410),
        },
        groupInnerContainer: {
            flexDir: 'column',
            gap: 'xsmall.base',
            direction: 'ltr',
            alignItems: 'flex-start',
        },
        groupListContainer: {
            flexDir: 'column',
            alignItems: 'flex-start',
            gap: 'medium.base',
        },
        groupLinkContainer: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: {
                base: 'xxsmall.base',
                sm: 'xxsmall.base',
                md: 'xxsmall.lg',
                lg: 'xxsmall.lg',
            },
        },
        titleContainer: {
            color: 'foreground.neutral.subtle',
            textStyle: 'body-xsmall',
            fontWeight: 'medium',
            textDecoration: 'none',
            paddingY: remCalc(4),
        },
        groupLinkButton: {
            variant: 'link-button-no-underline',
            textStyle: 'body-xxlarge',
            fontWeight: 'light',
            color: 'foreground.neutral.muted',
            textAlign: 'left',
            textDecoration: 'none',
            background: 'transparent',
            border: 'none',
            paddingX: '0',
            paddingY: '0',
            minHeight: 'auto',
            height: 'auto',
            justifyContent: 'flex-start',

            _hover: {
                textDecoration: 'none',
                color: 'foreground.neutral.default',
                background: 'transparent',
            },
            _active: {
                background: 'transparent',
            },
            _focus: {
                boxShadow: 'none',
            },
        },
        linkContainer: {
            flexDir: 'column',
            alignItems: 'flex-start',
            gap: 'xxsmall-1.base',
        },
        iconImage: {
            width: remCalc(12),
            height: remCalc(12),
        },
        iconLinkText: {
            textStyle: 'body-small',
            fontWeight: 'medium',
        },
        verticalCardWrapper: {
            width: remCalc(347),
            maxWidth: remCalc(347),
            minHeight: remCalc(462),
            maxHeight: remCalc(462),
            flexShrink: '0',
            overflow: 'hidden',
        },
        verticalCardsContainer: {
            gap: 'small.base',
            minW: '0',
            flexShrink: 0,
        },
        simpleCardsContainer: {
            width: remCalc(347),
            maxWidth: remCalc(347),
            flex: `0 1 ${remCalc(347)}`,
            minW: '0',
            minH: remCalc(462),
            height: '100%',
            flexDir: 'column',
            alignItems: 'stretch',
            gridAutoRows: remCalc(140),
            gap: 'small.base',
        },
        simpleCardWrapper: {
            minHeight: '0',
            minW: '0',
            overflow: 'hidden',
        },
    },
    variants: {
        'template-one': {
            gridInnerContainer: {
                minW: remCalc(135),
            },
            groupOuterContainer: {
                minHeight: {base: remCalc(410), md: '0', lg: remCalc(410)},
                flexShrink: {md: 0},
                textAlign: {md: 'left'},
            },
            groupInnerContainer: {
                width: {md: '100%'},
                alignItems: {md: 'flex-start'},
                textAlign: {md: 'left'},
            },
            ...fullWidthGroupLists,
            ...leftAlignedGroupText,
            verticalCardsContainer: {
                minW: '0',
                flexShrink: 1,
                sx: {
                    '@media screen and (max-width: 1320px) and (min-width: 1200px)': {
                        flexShrink: 0,
                    },
                },
            },
            verticalCardWrapper: {
                width: {base: remCalc(183), md: remCalc(304), lg: remCalc(347)},
                maxWidth: {base: remCalc(183), md: remCalc(304), lg: remCalc(347)},
                height: {base: remCalc(120), md: remCalc(406), lg: remCalc(462)},
                minHeight: {base: remCalc(120), md: remCalc(406), lg: remCalc(462)},
                maxHeight: {base: remCalc(120), md: remCalc(406), lg: remCalc(462)},
                minW: '0',
                flexShrink: 0,
            },
        },
        'template-two': {
            categoryGroupColumn: {
                width: remCalc(343),
                maxWidth: remCalc(343),
                minW: '0',
                flex: `0 1 ${remCalc(343)}`,
            },
            listWrapper: {
                flex: '1 0 auto',
            },
            verticalCardWrapper: {
                width: {base: remCalc(370), md: remCalc(624), lg: remCalc(714)},
                maxWidth: {base: remCalc(370), md: remCalc(624), lg: remCalc(714)},
                height: {base: remCalc(278), md: remCalc(468), lg: remCalc(536)},
                minHeight: {base: remCalc(278), md: remCalc(468), lg: remCalc(536)},
                maxHeight: {base: remCalc(278), md: remCalc(468), lg: remCalc(536)},
                flexShrink: 0,
                overflow: 'hidden',
            },
        },
        'template-three': {
            sideColumn: {
                maxWidth: remCalc(293),
                minW: '0',
                flex: `0 1 ${remCalc(293)}`,
            },
            categoryGroupColumn: {
                width: '100%',
                minW: '0',
            },
            listWrapper: {
                flex: '1 0 auto',
                textAlign: {md: 'left'},
            },
            ...leftAlignedGroupText,
            verticalCardWrapper: {
                width: 'auto',
                maxWidth: 'none',
                minW: '0',
                flexShrink: 0,
                overflow: 'hidden',
            },
            firstVerticalCard: {
                width: {base: remCalc(370), md: remCalc(619), lg: remCalc(714)},
                height: {base: remCalc(278), md: remCalc(464), lg: remCalc(536)},
                minHeight: {base: remCalc(278), md: remCalc(464), lg: remCalc(536)},
                maxHeight: {base: remCalc(278), md: remCalc(464), lg: remCalc(536)},
                flexShrink: 0,
            },
            secondVerticalCard: {
                width: {base: remCalc(370), md: remCalc(619), lg: remCalc(401)},
                height: {base: remCalc(494), md: remCalc(827), lg: remCalc(536)},
                minHeight: {base: remCalc(494), md: remCalc(827), lg: remCalc(536)},
                maxHeight: {base: remCalc(494), md: remCalc(827), lg: remCalc(536)},
                flexShrink: 0,
            },
        },
        'template-four': {
            layout: {
                alignItems: 'stretch',
                minW: '0',
                height: 'auto',
            },
            sideColumn: {
                flexDir: 'column',
                justifyContent: 'space-between',
                minW: '0',
                flex: `0 1 ${remCalc(185)}`,
                maxW: remCalc(185),
                gap: 'small.base',
            },
            cardsRow: {
                flexDirection: 'row',
                alignItems: 'stretch',
                justifyContent: 'flex-end',
                gap: remCalc(20),
                minW: '0',
                flex: '1 1 0',
            },
            groupOuterContainer: {
                maxWidth: 'none',
                minHeight: '0',
                flexShrink: 0,
                textAlign: {md: 'left'},
            },
            groupInnerContainer: {
                width: {md: '100%'},
                alignItems: {md: 'flex-start'},
                textAlign: {md: 'left'},
            },
            ...fullWidthGroupLists,
            ...leftAlignedGroupText,
            verticalCardWrapper: {
                flex: '1 0 0',
                minW: '0',
                minHeight: '0',
                width: '100%',
                maxWidth: remCalc(362),
                maxHeight: remCalc(536),
                aspectRatio: '362 / 536',
                overflow: 'hidden',
            },
            simpleCardWrapper: {
                flex: '1 1 0',
                minW: '0',
                width: '100%',
                maxW: remCalc(362),
                maxH: remCalc(536),
                aspectRatio: '362 / 536',
                overflow: 'hidden',
            },
        },
    },
}
